import { Vector3 } from '@babylonjs/core';
import { World } from '../core/World';
import { Block } from '../voxel/Block';

export interface PlayerBounds {
  width: number;
  height: number;
  eyeHeight: number;
}

const DEFAULT_BOUNDS: PlayerBounds = {
  width: 0.6,
  height: 1.8,
  eyeHeight: 1.6,
};

export class PlayerPhysics {
  private world: World | null;
  private bounds: PlayerBounds;

  public position: Vector3 = new Vector3(0, 10, 0);
  public velocity: Vector3 = new Vector3(0, 0, 0);

  public isGrounded: boolean = false;
  public isInWater: boolean = false;

  // Physics constants
  public readonly gravity: number = -25;
  public readonly jumpForce: number = 9;
  public readonly terminalVelocity: number = -50;
  public readonly groundFriction: number = 10;
  public readonly airFriction: number = 2;
  public readonly waterFriction: number = 8;

  constructor(world: World | null, bounds?: Partial<PlayerBounds>) {
    this.world = world;
    this.bounds = { ...DEFAULT_BOUNDS, ...bounds };
  }

  public update(deltaTime: number): void {
    // Check if in water
    this.isInWater = this.checkWater();

    // Apply gravity (reduced in water)
    if (!this.isGrounded) {
      const gravityMultiplier = this.isInWater ? 0.3 : 1;
      this.velocity.y += this.gravity * gravityMultiplier * deltaTime;

      // Terminal velocity
      if (this.velocity.y < this.terminalVelocity) {
        this.velocity.y = this.terminalVelocity;
      }
    }

    // Apply friction
    const friction = this.isInWater
      ? this.waterFriction
      : this.isGrounded
        ? this.groundFriction
        : this.airFriction;

    this.velocity.x *= Math.max(0, 1 - friction * deltaTime);
    this.velocity.z *= Math.max(0, 1 - friction * deltaTime);

    // Move with collision detection
    this.moveWithCollision(deltaTime);
  }

  private moveWithCollision(deltaTime: number): void {
    const movement = this.velocity.scale(deltaTime);

    // Check X movement
    if (Math.abs(movement.x) > 0.001) {
      const newX = this.position.x + movement.x;
      if (!this.checkCollisionAt(newX, this.position.y, this.position.z)) {
        this.position.x = newX;
      } else {
        this.velocity.x = 0;
      }
    }

    // Check Y movement
    if (Math.abs(movement.y) > 0.001) {
      const newY = this.position.y + movement.y;
      if (!this.checkCollisionAt(this.position.x, newY, this.position.z)) {
        this.position.y = newY;
        this.isGrounded = false;
      } else {
        if (this.velocity.y < 0) {
          this.isGrounded = true;
          // Snap to top of ground block
          // Find the ground level by checking from current position down
          const groundY = this.findGroundLevel(this.position.x, this.position.y, this.position.z);
          this.position.y = groundY;
        }
        this.velocity.y = 0;
      }
    }

    // Check Z movement
    if (Math.abs(movement.z) > 0.001) {
      const newZ = this.position.z + movement.z;
      if (!this.checkCollisionAt(this.position.x, this.position.y, newZ)) {
        this.position.z = newZ;
      } else {
        this.velocity.z = 0;
      }
    }

    // Ground check when standing still
    if (this.velocity.y === 0) {
      this.isGrounded = this.checkGrounded();
    }
  }

  private checkCollisionAt(x: number, y: number, z: number): boolean {
    // No voxel collision if no world (e.g., Obby mode uses mesh collision)
    if (!this.world) return false;

    const halfWidth = this.bounds.width / 2;
    const height = this.bounds.height;

    // Check corners of bounding box at feet level
    const checkPoints = [
      [x - halfWidth, y, z - halfWidth],
      [x + halfWidth, y, z - halfWidth],
      [x - halfWidth, y, z + halfWidth],
      [x + halfWidth, y, z + halfWidth],
      // Check at knee level
      [x - halfWidth, y + 0.5, z - halfWidth],
      [x + halfWidth, y + 0.5, z - halfWidth],
      [x - halfWidth, y + 0.5, z + halfWidth],
      [x + halfWidth, y + 0.5, z + halfWidth],
      // Check at head level
      [x - halfWidth, y + height - 0.1, z - halfWidth],
      [x + halfWidth, y + height - 0.1, z - halfWidth],
      [x - halfWidth, y + height - 0.1, z + halfWidth],
      [x + halfWidth, y + height - 0.1, z + halfWidth],
    ];

    for (const [px, py, pz] of checkPoints) {
      const block = this.world.getBlock(px, py, pz);
      if (Block.isSolid(block)) {
        return true;
      }
    }

    return false;
  }

  private checkGrounded(): boolean {
    // No voxel ground check if no world (Obby uses platform collision)
    if (!this.world) return false;

    const halfWidth = this.bounds.width / 2;
    const checkY = this.position.y - 0.1;

    // Check corners below feet
    const checkPoints = [
      [this.position.x - halfWidth, checkY, this.position.z - halfWidth],
      [this.position.x + halfWidth, checkY, this.position.z - halfWidth],
      [this.position.x - halfWidth, checkY, this.position.z + halfWidth],
      [this.position.x + halfWidth, checkY, this.position.z + halfWidth],
      [this.position.x, checkY, this.position.z], // Center
    ];

    for (const [px, py, pz] of checkPoints) {
      const block = this.world.getBlock(px, py, pz);
      if (Block.isSolid(block)) {
        return true;
      }
    }

    return false;
  }

  private checkWater(): boolean {
    // No water in non-voxel modes
    if (!this.world) return false;

    // Check if player's body is in water
    const waterCheckY = this.position.y + 0.5;
    const block = this.world.getBlock(this.position.x, waterCheckY, this.position.z);
    return block === Block.WATER;
  }

  private findGroundLevel(x: number, y: number, z: number): number {
    // No voxel ground in non-voxel modes
    if (!this.world) return y;

    // Start from current Y and find the top of the highest solid block below
    const startY = Math.floor(y);

    for (let checkY = startY; checkY >= 0; checkY--) {
      const block = this.world.getBlock(x, checkY, z);
      if (Block.isSolid(block)) {
        // Found solid block, player should stand on top (checkY + 1)
        return checkY + 1;
      }
    }

    // No ground found, return 0
    return 0;
  }

  public jump(): boolean {
    if (this.isGrounded) {
      this.velocity.y = this.jumpForce;
      this.isGrounded = false;
      return true;
    } else if (this.isInWater) {
      // Swimming - can always push up
      this.velocity.y = this.jumpForce * 0.5;
      return true;
    }
    return false;
  }

  public applyMovement(direction: Vector3, speed: number): void {
    const moveSpeed = this.isInWater ? speed * 0.6 : speed;
    const acceleration = this.isGrounded ? 50 : 20;

    this.velocity.x += direction.x * moveSpeed * acceleration * (1 / 60);
    this.velocity.z += direction.z * moveSpeed * acceleration * (1 / 60);

    // Clamp horizontal velocity
    const horizontalSpeed = Math.sqrt(this.velocity.x ** 2 + this.velocity.z ** 2);
    const maxSpeed = this.isInWater ? moveSpeed * 0.6 : moveSpeed;

    if (horizontalSpeed > maxSpeed) {
      const scale = maxSpeed / horizontalSpeed;
      this.velocity.x *= scale;
      this.velocity.z *= scale;
    }
  }

  public setPosition(position: Vector3): void {
    this.position = position.clone();
    this.velocity.set(0, 0, 0);
  }

  public getEyePosition(): Vector3 {
    return new Vector3(
      this.position.x,
      this.position.y + this.bounds.eyeHeight,
      this.position.z
    );
  }

  public getFeetPosition(): Vector3 {
    return this.position.clone();
  }

  public getBounds(): PlayerBounds {
    return { ...this.bounds };
  }

  // External ground detection (for non-voxel modes like Obby)
  public setGroundedAt(groundY: number): void {
    this.isGrounded = true;
    this.position.y = groundY;
    if (this.velocity.y < 0) {
      this.velocity.y = 0;
    }
  }

  public clearGrounded(): void {
    // Called when player is no longer on a platform
    // isGrounded will be set to false by physics when falling
  }
}
